import { checkboxClasses } from '@mui/material';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/system';
import { OrderSource, OrderState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { ReactNode } from 'react';

import Checkbox from '~components/Checkbox/Checkbox';
import ChildCompanyBadge from '~components/ChildCompanyBadge/ChildCompanyBadge';
import DepartmentBadge from '~components/Common/DepartmentBadge';
import EmailIntakeBadge from '~components/Common/EmailIntakeBadge';
import PrevailingWageBadge from '~components/Common/PrevailingWageBadge';
import { Column, FlexColumn } from '~components/Order/ordersDispatchStyledComponents';
import OrderCardSubtitle from '~components/OrderCard/OrderCardSubtitle';
import OrderCardTitle from '~components/OrderCard/OrderCardTitle';
import OrderSearchMatchBadge from '~components/OrderSearchMatchBadge/OrderSearchMatchBadge';
import { JobCardPdfRecipientType } from '~hooks/useJobCardPdf/models';
import { LoadDigestLoadFilter } from '~hooks/useLoadDigests/models';
import { Order } from '~hooks/useOrders/models';
import {
  AcceptAllJobsCTA,
  Alerts,
  BulkAssignMultipleCTA,
  Contact,
  CycleTime,
  DeliveredLoadsSummary,
  DeliveredQuantitiesSummary,
  DropffWaypoint,
  EditOrderCTA,
  EquipmentType,
  InternalNotes,
  JobNotes,
  OrderNotes,
  OrderUnreadMessagesCTA,
  PickupWaypoint,
  RejectReasonBadge,
  Requester,
  StartTime,
  Status,
  ThreeDotsMenu,
  TrucksSummary,
} from '~pages/Dispatch/components/OrderDispatchCardFragments';
import { rootStore } from '~store';
import { spacing } from '~theme/AppTheme';

export enum OrdersDispatchColumnKey {
  CHECKBOX = 'checkbox',
  DISPATCH = 'dispatch',
  COMPANY = 'company',
  TRUCKS_AND_TYPES = 'trucksandtypes',
  FOR = 'for',
  START = 'start',
  STATUS = 'status',
  WAYPOINTS = 'waypoints',
  MATERIAL = 'material',
  CYCLE = 'cycle',
  ALERTS = 'alerts',
  ACTIONS = 'actions',
}

export const orderDispatchColumns: Record<
  OrdersDispatchColumnKey,
  { title: ReactNode; size: string; sx?: SxProps; condition?: () => boolean }
> = {
  [OrdersDispatchColumnKey.CHECKBOX]: {
    title: '',
    size: '20px',
    sx: { px: 0 },
  },
  [OrdersDispatchColumnKey.DISPATCH]: {
    title: t('dispatch.dispatch_v2.dispatch_ext_id'),
    size: '1fr',
  },
  [OrdersDispatchColumnKey.COMPANY]: {
    title: t('dispatch.dispatch_v2.company'),
    size: '2fr',
    condition: () => rootStore?.userStore?.userCompanies?.length > 1 || false,
  },
  [OrdersDispatchColumnKey.FOR]: {
    title: t('dispatch.dispatch_v2.for_and_customer'),
    size: '2fr',
  },
  [OrdersDispatchColumnKey.TRUCKS_AND_TYPES]: {
    title: t('dispatch.dispatch_v2.trucks_and_type'),
    size: '2fr',
  },
  [OrdersDispatchColumnKey.START]: {
    title: t('dispatch.dispatch_v2.start'),
    size: '1fr',
  },
  [OrdersDispatchColumnKey.CYCLE]: {
    title: t('dispatch.dispatch_v2.cycle_time'),
    size: '80px',
  },
  [OrdersDispatchColumnKey.STATUS]: {
    title: t('form_fields.status'),
    size: '105px',
    sx: { display: 'flex', gap: spacing(0.5), px: 0 },
  },
  [OrdersDispatchColumnKey.WAYPOINTS]: {
    title: t('dispatch.dispatch_v2.pick_up_and_drop_off'),
    size: '2fr',
  },
  [OrdersDispatchColumnKey.MATERIAL]: {
    title: t('dispatch.dispatch_v2.material_delivered'),
    size: '1fr',
  },
  [OrdersDispatchColumnKey.ALERTS]: {
    title: t('actions.alerts'),
    size: '1fr',
  },
  [OrdersDispatchColumnKey.ACTIONS]: {
    title: '',
    size: '120px',
    sx: { display: 'flex', justifyContent: 'flex-end', px: 0 },
  },
} as const;

export const makeOrderDispatchCardTemplateColumns = (
  isSidebarCollapsed: boolean = false,
) => {
  if (isSidebarCollapsed) {
    return ['0px', '1fr', 'auto'].join(' ');
  }

  return [
    orderDispatchColumns[OrdersDispatchColumnKey.CHECKBOX].size,
    '1fr',
    'auto',
  ].join(' ');
};

interface OrderDispatchColumnProps {
  order: Order;
}

interface CheckboxColumnProps {
  isChecked?: boolean;
  isDisabled?: boolean;
  isIntermediate?: boolean;
  onCheckedStateChange?: () => void;
}

export function CheckboxColumn({
  isChecked,
  isDisabled,
  isIntermediate,
  onCheckedStateChange,
}: CheckboxColumnProps) {
  return (
    <Column columnKey={OrdersDispatchColumnKey.CHECKBOX}>
      <Checkbox
        isChecked={isChecked}
        isDisabled={isDisabled}
        isIntermediate={isIntermediate}
        onChange={onCheckedStateChange}
        sx={{ [`&.${checkboxClasses.root}`]: { mt: '-10px' } }}
      />
    </Column>
  );
}

interface MainColumnProps extends OrderDispatchColumnProps {
  hideMaterialsSettingEnabled?: boolean;
  searchMatches?: string[];
}

export function MainColumn({
  hideMaterialsSettingEnabled,
  order,
  searchMatches,
}: MainColumnProps) {
  return (
    <Column columnKey={OrdersDispatchColumnKey.DISPATCH}>
      <FlexColumn>
        {/* Title and the "Found in:" search-match badge share one row so surfacing
            WHY the card matched adds no vertical height to the collapsed card. */}
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <OrderCardTitle enableAttachmentPreview order={order} sx={{ mt: '-3px' }} />
          {searchMatches && searchMatches.length > 0 && (
            <OrderSearchMatchBadge searchMatches={searchMatches} />
          )}
        </Box>
        <OrderCardSubtitle
          hideMaterialsSettingEnabled={hideMaterialsSettingEnabled}
          order={order}
        />
      </FlexColumn>
    </Column>
  );
}

export function OrderIndicatorBadges({ order }: OrderDispatchColumnProps) {
  return (
    <>
      <ChildCompanyBadge entity={order} />
      {order.prevailingWage && <PrevailingWageBadge />}
      {order.source === OrderSource.EMAIL_INTAKE && <EmailIntakeBadge />}
    </>
  );
}

interface OrderStatusControlsProps extends OrderDispatchColumnProps {
  onAccept: () => void;
  onCancel: () => void;
  onOrderStateChange: (nextOrderState: OrderState) => Promise<void>;
  onReject: () => void;
}

export function OrderStatusControls({
  onAccept,
  onCancel,
  onOrderStateChange,
  onReject,
  order,
}: OrderStatusControlsProps) {
  return (
    <>
      <AcceptAllJobsCTA order={order} />
      <Status
        onAccept={onAccept}
        onOrderStateChange={onOrderStateChange}
        onReject={onReject}
        onCancel={onCancel}
        order={order}
      />
      <RejectReasonBadge order={order} />

      <Alerts order={order} />
    </>
  );
}

interface OrderBulkAssignAndMessagesButtonsProps extends OrderDispatchColumnProps {
  onBulkAssignMultiple?: () => void;
  onOpenMessagingTab: () => void;
}

export function OrderBulkAssignAndMessagesButtons({
  onBulkAssignMultiple,
  onOpenMessagingTab,
  order,
}: OrderBulkAssignAndMessagesButtonsProps) {
  return (
    <>
      <BulkAssignMultipleCTA order={order} onClick={onBulkAssignMultiple} />

      <OrderUnreadMessagesCTA order={order} onClick={onOpenMessagingTab} />
    </>
  );
}

interface OrderEditAndMenuButtonsProps extends OrderDispatchColumnProps {
  onCancel: () => void;
  onCloneOrder: () => void;
  onCopyAndEdit: () => void;
  onDownloadFiles?: () => void;
  onDownloadJobCardPdfs?: (recipientType: JobCardPdfRecipientType) => void;
  onEdit: () => void;
  onEmailLoadDigest?: () => void;
  onOpenMessagingTab: () => void;
  onTextAllDrivers: () => void;
  onViewLoadDigest?: (loadFilter: LoadDigestLoadFilter) => void;
}

export function OrderEditAndMenuButtons({
  onCancel,
  onCloneOrder,
  onCopyAndEdit,
  onDownloadFiles,
  onDownloadJobCardPdfs,
  onEdit,
  onEmailLoadDigest,
  onOpenMessagingTab,
  onTextAllDrivers,
  onViewLoadDigest,
  order,
}: OrderEditAndMenuButtonsProps) {
  return (
    <>
      <EditOrderCTA order={order} onClick={onEdit} />

      <ThreeDotsMenu
        onCancelClick={onCancel}
        onCloneOrderClick={onCloneOrder}
        onCopyAndEditClick={onCopyAndEdit}
        onDownloadFilesClick={onDownloadFiles}
        onDownloadJobCardPdfsClick={onDownloadJobCardPdfs}
        onEmailLoadDigestClick={onEmailLoadDigest}
        onOpenMessagingTabClick={onOpenMessagingTab}
        onTextAllDriversClick={onTextAllDrivers}
        onViewLoadDigestClick={onViewLoadDigest}
        order={order}
      />
    </>
  );
}

interface ActionsColumnProps
  extends
    OrderStatusControlsProps,
    OrderBulkAssignAndMessagesButtonsProps,
    OrderEditAndMenuButtonsProps {}

export function ActionsColumn({
  onAccept,
  onBulkAssignMultiple,
  onCancel,
  onCloneOrder,
  onCopyAndEdit,
  onDownloadFiles,
  onDownloadJobCardPdfs,
  onEdit,
  onEmailLoadDigest,
  onOpenMessagingTab,
  onOrderStateChange,
  onReject,
  onTextAllDrivers,
  onViewLoadDigest,
  order,
}: ActionsColumnProps) {
  return (
    <Column
      columnKey={OrdersDispatchColumnKey.ACTIONS}
      sx={{ display: 'flex', alignItems: 'flex-start', pr: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DepartmentBadge department={order.department ?? undefined} sx={{ mt: 0.25 }} />
        <OrderIndicatorBadges order={order} />
        <OrderStatusControls
          onAccept={onAccept}
          onCancel={onCancel}
          onOrderStateChange={onOrderStateChange}
          onReject={onReject}
          order={order}
        />

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <OrderBulkAssignAndMessagesButtons
            onBulkAssignMultiple={onBulkAssignMultiple}
            onOpenMessagingTab={onOpenMessagingTab}
            order={order}
          />

          <OrderEditAndMenuButtons
            onCancel={onCancel}
            onCloneOrder={onCloneOrder}
            onCopyAndEdit={onCopyAndEdit}
            onDownloadFiles={onDownloadFiles}
            onDownloadJobCardPdfs={onDownloadJobCardPdfs}
            onEdit={onEdit}
            onEmailLoadDigest={onEmailLoadDigest}
            onOpenMessagingTab={onOpenMessagingTab}
            onTextAllDrivers={onTextAllDrivers}
            onViewLoadDigest={onViewLoadDigest}
            order={order}
          />
        </Box>
      </Box>
    </Column>
  );
}

interface BottomColumnProps extends OrderDispatchColumnProps {
  hideEquipmentTypeSettingEnabled?: boolean;
  hideOrderStartTimeSettingEnabled?: boolean;
  hidePickupAndDropoffColumnSettingEnabled?: boolean;
  omitWrapper?: boolean;
  statsSlot?: React.ReactNode;
}

export function BottomColumn({
  hideEquipmentTypeSettingEnabled,
  hideOrderStartTimeSettingEnabled,
  hidePickupAndDropoffColumnSettingEnabled,
  omitWrapper,
  order,
  statsSlot,
}: BottomColumnProps) {
  const content = (
    <Box sx={{ display: 'flex', gap: 3, justifyContent: 'space-between', width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 3, minWidth: 0, overflow: 'hidden' }}>
        <FlexColumn sx={{ minWidth: 200, maxWidth: 300 }}>
          {!hideOrderStartTimeSettingEnabled && <StartTime order={order} />}

          <Requester order={order} />
        </FlexColumn>

        <FlexColumn sx={{ minWidth: 200, maxWidth: 300 }}>
          {!hideEquipmentTypeSettingEnabled && <EquipmentType order={order} />}
          <Contact entity={order} />
        </FlexColumn>

        {!hidePickupAndDropoffColumnSettingEnabled && (
          <FlexColumn sx={{ minWidth: 200, maxWidth: 300 }}>
            <PickupWaypoint entity={order} />
            <DropffWaypoint entity={order} />
          </FlexColumn>
        )}

        <FlexColumn sx={{ minWidth: 200, maxWidth: 300 }}>
          <OrderNotes order={order} />
          <InternalNotes order={order} />
          <JobNotes order={order} />
        </FlexColumn>
      </Box>

      <Box sx={{ display: 'flex', gap: 3 }}>
        {statsSlot ?? (
          <>
            <CycleTime order={order} />

            <TrucksSummary order={order} />

            <DeliveredLoadsSummary order={order} />
            <DeliveredQuantitiesSummary order={order} />
          </>
        )}
      </Box>
    </Box>
  );

  if (omitWrapper) {
    return content;
  }

  return (
    <Column
      columnKey={OrdersDispatchColumnKey.DISPATCH}
      sx={{
        alignItems: 'flex-start',
        display: 'flex',
        gap: 3,
        gridColumn: 'span 3',
        justifyContent: 'space-between',
        mx: -1,
        pl: `calc(${orderDispatchColumns[OrdersDispatchColumnKey.CHECKBOX].size} + ${spacing(2)})`,
        pr: 0,
        width: '100%',
      }}
    >
      {content}
    </Column>
  );
}
